import "./ToolTip.scss";
import {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
} from "react";

const ToolTip = forwardRef((props, ref) => {
  const { manualControl = false } = props; // manualControl means parent handles all events
  const [isVisible, setIsVisible] = useState(false);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [arrow, setArrow] = useState(null);
  const [content, setContent] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [_hovering, setHovering] = useState(false);

  const toolTipRef = useRef(null);
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);

  useImperativeHandle(ref, () => ({
    open({ arrow, content, triggerElement }) {
      // Clear any pending timeout from previous hover
      clearTimeout(timeoutRef.current);

      // Remove listeners from previous trigger element
      if (triggerRef.current && triggerRef.current !== triggerElement) {
        triggerRef.current.removeEventListener("mouseenter", handleMouseEnter);
        triggerRef.current.removeEventListener("mouseleave", handleMouseLeave);
      }

      setArrow(arrow);
      setContent(content);
      triggerRef.current = triggerElement;
      setIsVisible(true);
      setHasMeasured(false);
      setHovering(true);

      // Only add event listeners if not in manual control mode
      if (!manualControl && triggerRef.current) {
        triggerRef.current.addEventListener("mouseenter", handleMouseEnter);
        triggerRef.current.addEventListener("mouseleave", handleMouseLeave);
      }
    },
    close() {
      clearTimeout(timeoutRef.current);
      setIsVisible(false);
      setHovering(false);

      if (triggerRef.current) {
        triggerRef.current.removeEventListener("mouseenter", handleMouseEnter);
        triggerRef.current.removeEventListener("mouseleave", handleMouseLeave);
        triggerRef.current = null;
      }
    },
  }));

  useEffect(() => {
    if (isVisible && !hasMeasured && toolTipRef.current && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const toolTipRect = toolTipRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (arrow) {
        case "top-left":
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2 - 28;
          break;
        case "top-center":
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2 - toolTipRect.width / 2;
          break;
        case "top-right":
          top = rect.bottom + 8;
          left = rect.left - toolTipRect.width + rect.width / 2 + 28;
          break;

        case "bottom-left":
          top = rect.top - toolTipRect.height - 8;
          left = rect.left + rect.width / 2 - 28;
          break;
        case "bottom-center":
          top = rect.top - toolTipRect.height - 8;
          left = rect.left + rect.width / 2 - toolTipRect.width / 2;
          break;
        case "bottom-right":
          top = rect.top - toolTipRect.height - 8;
          left = rect.left - toolTipRect.width + rect.width / 2 + 28;
          break;

        case "left-top":
          top = rect.top - toolTipRect.height + rect.height / 2 + 28;
          left = rect.left + rect.width + 8;
          break;
        case "left-center":
          top = rect.top - toolTipRect.height / 2 + rect.height / 2;
          left = rect.left + rect.width + 8;
          break;
        case "left-bottom":
          top = rect.top + rect.height / 2 - 28;
          left = rect.left + rect.width + 8;
          break;

        case "right-top":
          top = rect.top - toolTipRect.height + rect.height / 2 + 28;
          left = rect.left - toolTipRect.width - 8;
          break;
        case "right-center":
          top = rect.top - toolTipRect.height / 2 + rect.height / 2;
          left = rect.left - toolTipRect.width - 8;
          break;
        case "right-bottom":
          top = rect.top + rect.height / 2 - 28;
          left = rect.left - toolTipRect.width - 8;
          break;

        default:
          top = window.innerHeight / 2 - toolTipRect.height / 2 + scrollY;
          left = window.innerWidth / 2 - toolTipRect.width / 2 + scrollX;
          break;
      }

      setPosition({ top, left });
      setHasMeasured(true);
    }
  }, [isVisible, hasMeasured, arrow]);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setHovering(true);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHovering(false);
      setIsVisible(false);
    }, 10);
  };

  // if (!isVisible && !hovering) return null;

  return (
    <div
      className={`tooltip ${arrow || ""} ${isVisible ? "visible" : ""}`}
      ref={toolTipRef}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: hasMeasured ? "visible" : "hidden",
        position: "fixed",
        transition: "opacity 0.2s ease-in-out",
        opacity: isVisible ? 1 : 0,
        zIndex: 9999,
      }}
    >
      <div className="tooltip-content">{content}</div>
    </div>
  );
});

export default ToolTip;
